import { useState, useEffect } from 'react';
import Select from 'react-select';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Loader from '../../../components/globalloaders/Loader';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { Bars3Icon, EllipsisVerticalIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, UserIcon, BuildingOfficeIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { categoryOptions, entryStatusOptions, entryTypeOptions } from '../../../constants/options';
import { formatLabel, encryptPayload, decryptPayload, formatTableDateTime, formatDateTime, toDateTimeLocal, toDateTimeIso, capitalizeWords, splitPhone } from '../../../constants/helpers';
import { countriesOptions } from '../../../constants/options';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import OpCodeDialog from "../../../components/dialogbox/OpCodeDialog";
import FallBack from '../../../components/dialogbox/FallBack';
import api from '../../../utils/auth/api';
import EntitySelector from '../components/entry/EntitySelector';
import AgreementAdd from '../components/agreement/AgreementAdd';

const Entry = ({ setIsMobileOpen }) => {

  const { isLoading, setIsLoading } = useLoading();
  const [loaders, setLoaders] = useState({
    fetch: false,
    update: false,
    details: false,
    search: false,
    pdfFilter: false,
  });
  const [dialog, setDialog] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    confirmText: "",
    action: () => { },
  });
  const [opCodeDialog, setOpCodeDialog] = useState({
    open: false,
    id: null,
  });

  const [entryData, setEntryData] = useState([]);

  // Menu List State
  const [menuState, setMenuState] = useState({
    open: false,
    rowId: null,
    rowData: null,
    position: { top: 0, left: 0 },
  });
  const openMenu = ({ id, position, row }) => {
    setMenuState({
      open: true,
      rowId: id,
      rowData: row,
      position,
    });
  };
  const closeMenu = () => {
    setMenuState({
      open: false,
      rowId: null,
      rowData: null,
      position: { top: 0, left: 0 },
    });
  };

  // For Search Quick & Deep
  const [quickResults, setQuickResults] = useState([]);
  const [deepResults, setDeepResults] = useState([]);
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState("quick");
  const [isQuickSearching, setIsQuickSearching] = useState(false);
  const [totalBackendPg, setTotalBackendPg] = useState(1);
  const getDisplayData = () => {
    if (searchMode === "quick") {
      return isQuickSearching ? quickResults : entryData;
    }
    if (searchMode === "deep") {
      return deepResults;
    }
    return entryData;
  };

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const displayData = getDisplayData();
  const [totalPages, setTotalPages] = useState(1);
  const paginatedData = displayData;
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalBackendPg);
  useEffect(() => {
    setQuery("");
    setQuickResults([]);
    setDeepResults([]);
    setIsQuickSearching(false);
    if (searchMode === "quick") {
      setCurrentPage(1);
      setTotalPages(Math.ceil(totalBackendPg / itemsPerPage));
    } else if (searchMode === "deep") {
      setCurrentPage(1);
      setTotalPages(1);
    }
  }, [searchMode, totalBackendPg, itemsPerPage]);

  // Details DialogBox with Api Call By ID 
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [formData1, setFormData1] = useState({});
  const handleViewOpen = async (id) => {
    setIsViewOpen(true);
    setLoaders(prev => ({ ...prev, details: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/entry/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setFormData1(decryptedData);
      }
      else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Entry details:', error);
    } finally {
      setLoaders(prev => ({ ...prev, details: false }));
      return;
    }
  };
  const handleViewClose = () => {
    setIsViewOpen(false);
    setFormData1({});
  };

  // Update DialogBox with Api Call and Send
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [formData2, setFormData2] = useState({
    id: '',
    employeeRef: '',
    clientRef: '',
    clientName: '',
    clientAddress: '',
    countryCode: '',
    clientPhone: '',
    agreementRef: null,
    employeeObj: null,
    clientObj: null,
    agreementObj: null,
    category: '',
    date: '',
    type: '',
    status: '',
  });
  const handleSelectUpdate = (selectedOption, { name }) => {
    setFormData2({
      ...formData2,
      [name]: selectedOption ? selectedOption.value : "",
    });
  };
  const handleChangeUpdate = (e) => {
    const { name, value } = e.target;
    const autoCapFields = ["clientName", "clientAddress"];
    setFormData2({
      ...formData2,
      [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
    });
  };
  const handleUpdateOpen = async (id) => {
    setIsUpdateOpen(true);
    setLoaders(prev => ({ ...prev, update: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/entry/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        const { countryCode, phone } = splitPhone(decryptedData?.clientPhone || "");
        setFormData2({
          id,
          category: decryptedData?.category || '',
          date: toDateTimeLocal(decryptedData?.date) || '',
          type: decryptedData?.type || '',
          status: decryptedData?.status || '',
          employeeRef: decryptedData?.employeeRef?._id || '',
          clientRef: decryptedData?.clientRef?._id || '',
          clientName: decryptedData?.clientName || '',
          clientPhone: phone || '',
          countryCode: countryCode || 'PK',
          clientAddress: decryptedData?.clientAddress || '',
          agreementRef: decryptedData?.agreementRef?._id || '',
          employeeObj: decryptedData?.employeeRef || null,
          clientObj: decryptedData?.clientRef || null,
          agreementObj: decryptedData?.agreementRef || null,
        });
      }
      else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Entry edit:', error);
    } finally {
      setLoaders(prev => ({ ...prev, update: false }));
      return;
    }
  };
  const handleUpdateClose = () => {
    setIsUpdateOpen(false);
    setFormData2({
      id: '',
      employeeRef: '',
      clientRef: '',
      agreementRef: null,
      employeeObj: null,
      clientObj: null,
      agreementObj: null,
      category: '',
      date: '',
      type: '',
      status: '',
    });
  };
  // Update API
  const handleUpdate = async (id) => {

    if (isLoading) return;

    if (!id) {
      toast.error('Unable to update: ID is missing');
      return;
    };

    if (!formData2.category) {
      toast.warning("Category is required.");
      return;
    }
    if (!formData2.date) {
      toast.warning("Date is required.");
      return;
    }
    if (!formData2.type) {
      toast.warning("Entry Type is required.");
      return;
    }
    if (!formData2.status) {
      toast.warning("Entry Status is required.");
      return;
    }
    if (!formData2.employeeRef) {
      toast.warning("Employee is required.");
      return;
    }
    if (!formData2.clientRef || !formData2.clientName || !formData2.countryCode || !formData2.clientPhone || !formData2.clientAddress) {
      toast.warning("Client is required.");
      return;
    }
    if (formData2.type === "replace" && !formData2.agreementRef) {
      toast.warning("Agreement is required.");
      return;
    }

    const { employeeObj, clientObj, agreementObj, agreementRef, ...cleanData } = formData2;
    const payload = {
      ...cleanData,
      date: toDateTimeIso(formData2.date),
      ...(formData2.type === "replace" && { agreementRef }),
    };

    setIsLoading(true);

    const encryptedData = encryptPayload(payload);
    try {
      const response = await api.put(`${process.env.REACT_APP_URI}/entry/update/${id}`, { data: encryptedData });
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setEntryData((prevData) => prevData.map((data) => data._id === id ? { ...data, ...decryptedData } : data));
        toast.success(response.data.message);
        handleUpdateClose();
      } else {
        toast.error(response.data.message);
      }
    }
    catch (error) {
      console.log("Entry Updating Data failed:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error("Unable to Update Entry Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // QUICK SEARCH (local state)
  const handleQuickSearch = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (searchMode === "quick") {
      if (value.trim() === "") {
        setIsQuickSearching(false);
        setQuickResults(entryData);
      } else {
        setIsQuickSearching(true);
        const results = entryData.filter((s) =>
          s.employee?.toLowerCase().includes(value.toLowerCase()) ||
          s.client?.toLowerCase().includes(value.toLowerCase()) ||
          s.status?.toLowerCase().includes(value.toLowerCase()) ||
          s.entryId?.toLowerCase().includes(value.toLowerCase())
        );
        setQuickResults(results);
      }
    }
  };
  // DEEP SEARCH (API)
  const handleDeepSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      toast.warning("Enter value to search");
      return;
    }
    setLoaders(prev => ({ ...prev, search: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/entry/search`, {
        params: { value: searchQuery },
      });
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setDeepResults(decryptedData);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error deep searching entry:", error);
      toast.error(error?.response.data.message || "Deep search entry failed!");
    } finally {
      setLoaders(prev => ({ ...prev, search: false }));
      return;
    }
  };
  // Connect Search to Quick or Deep via searchMode
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchMode === "quick") {
      handleQuickSearch({ target: { value: query } });
    } else if (searchMode === "deep") {
      handleDeepSearch(query);
    }
  };

  // Fetch All API
  useEffect(() => {
    const fetchData = async (page = 1) => {
      setLoaders(prev => ({ ...prev, fetch: true }));
      try {
        const response = await api.get(`${process.env.REACT_APP_URI}/entry/get-all`, {
          params: { page, limit: itemsPerPage },
        });
        if (response.data.success) {
          const decryptedData = decryptPayload(response.data.encryptedData);
          setEntryData(decryptedData.data);
          setQuickResults(decryptedData.data);
          setTotalBackendPg(decryptedData.total);
          setTotalPages(Math.ceil(decryptedData.total / itemsPerPage));
        }
        else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching entries:', error);
      } finally {
        setLoaders(prev => ({ ...prev, fetch: false }));
        return;
      }
    };
    fetchData(currentPage);
  }, [currentPage]);

  // Delete API By ID
  const handleDelete = async (id, opcode) => {
    if (isLoading) return;
    setIsLoading(true);
    const payload = encryptPayload({ id, opcode });
    try {
      const response = await api.delete(`${process.env.REACT_APP_URI}/entry/delete`, { data: { data: payload } });
      if (response.data.success) {
        setEntryData((prevClientData) => prevClientData.filter((client) => client._id !== id));
        toast.success(response.data.message);
      }
    }
    catch (error) {
      console.log("Error occur while deleting entry:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        toast.error(data.message);
      } else {
        toast.error("Unable to Delete Entry Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // Add DialogBox Code & Store API
  const initialFormState = {
    employeeRef: '',
    clientRef: '',
    clientName: '',
    clientAddress: '',
    countryCode: 'PK',
    clientPhone: '',
    agreementRef: '',
    category: '',
    date: '',
    type: '',
    status: '',
  };
  const [formData, setFormData] = useState(initialFormState);
  const [isAddDialog, setAddDialog] = useState(false);
  const handleAddOpen = () => {
    setAddDialog(true);
  };
  const handleAddClose = () => {
    setAddDialog(false);
    setFormData(initialFormState);
  };
  const handleSelectChange = (selectedOption, { name }) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : "",
    });
  };
  const onChange = (e) => {
    const { name, value } = e.target;
    const autoCapFields = ["clientName", "clientAddress"];
    setFormData({
      ...formData,
      [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
    });
  };
  const handleSubmit = async () => {

    if (isLoading) return;

    // --- Required field checks ---
    if (!formData.category) {
      toast.warning("Category is required.");
      return;
    }
    if (!formData.date) {
      toast.warning("Date is required.");
      return;
    }
    if (!formData.type) {
      toast.warning("Entry Type is required.");
      return;
    }
    if (!formData.status) {
      toast.warning("Entry Status is required.");
      return;
    }
    if (!formData.employeeRef) {
      toast.warning("Employee is required.");
      return;
    }
    if (!formData.clientRef || !formData.clientName || !formData.countryCode || !formData.clientPhone || !formData.clientAddress) {
      toast.warning("Client is required.");
      return;
    }
    if (formData.type === "replace" && !formData.agreementRef) {
      toast.warning("Agreement is required.");
      return;
    }

    const payload = {
      ...formData,
      date: toDateTimeIso(formData.date),
    };
    const encryptedData = encryptPayload(payload);

    setIsLoading(true);
    try {
      const response = await api.post(`${process.env.REACT_APP_URI}/entry/create`, { data: encryptedData });
      if (response.data.success) {
        toast.success(response.data.message);
        const decryptedData = decryptPayload(response.data.encryptedData);
        setEntryData((prevData) => [decryptedData, ...prevData]);
        handleAddClose();
      }
      else {
        toast.error(response.data.message);
      }
    }
    catch (error) {
      console.log("Entry Creation failed:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error("Unable to Create Entry Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // Selector State handler
  const [selector, setSelector] = useState({
    open: false,
    type: null, // "employee" | "client" | "agreement"
    mode: "",
  });
  const openSelector = (type, mode) => {
    setSelector({ open: true, type, mode });
  };
  const handleEntitySelect = (item) => {
    const setter = selector.mode === "update" ? setFormData2 : setFormData;
    setter(prev => {
      if (selector.type === "employee") {
        return { ...prev, employeeRef: item._id, employeeObj: item };
      }

      if (selector.type === "client") {
        const { countryCode, phone } = splitPhone(item?.phone || "");
        return {
          ...prev,
          clientRef: item._id,
          clientObj: item,
          clientName: item.name || "",
          clientAddress: item.address || "",
          clientPhone: phone || "",
          countryCode: countryCode || "PK",
          agreementRef: null,
          agreementObj: null
        };
      }

      if (selector.type === "agreement") {
        return { ...prev, agreementRef: item._id, agreementObj: item };
      }
      return prev;
    });
  };

  // PDF Generation State, API & Template
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [pdfFilters, setPdfFilters] = useState({
    month: "",
    year: "",
  });
  const [pdfData, setPdfData] = useState([]);
  const handlePdfOpen = () => {
    setIsPdfOpen(true);
  };
  const handlePdfClose = () => {
    setIsPdfOpen(false);
    setPdfFilters({ month: "", year: "" });
    setPdfData([]);
  };
  const handlePdfFilterChange = (e) => {
    const { name, value } = e.target;
    setPdfFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handlePdfFetch = async () => {
    if (loaders.pdfFilter) return;
    if (!pdfFilters.month || !pdfFilters.year) {
      toast.warning("Select month and year");
      return;
    }
    setLoaders(prev => ({ ...prev, pdfFilter: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/entry/filter-pdf`, { params: { month: pdfFilters.month, year: pdfFilters.year } });
      if (response.data.success) {
        const decrypted = decryptPayload(response.data.encryptedData);
        setPdfData(decrypted);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching pdf entries data:', error);
      toast.error(error?.response.data.message || "Failed to fetch PDF data!");
    } finally {
      setLoaders(prev => ({ ...prev, pdfFilter: false }));
      return;
    }
  };
  const handleMonthlyPdf = () => {
    if (isLoading) return;
    if (!pdfFilters.month || !pdfFilters.year) {
      toast.warning("Select month and year");
      return;
    }
    if (!pdfData || pdfData.length === 0) {
      toast.warning("No data");
      return;
    }
    setIsLoading(true);
    try {
      const doc = new jsPDF("p", "mm", "a4"); // Portrait
      const pageWidth = doc.internal.pageSize.getWidth();
      const generationDate = new Date().toLocaleString();

      /* TITLE + META */
      doc.setFont("helvetica", "bold");
      doc.setTextColor(25, 25, 25);
      doc.setFontSize(16);
      doc.text("Monthly Entry Report", 10, 10);
      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(70, 70, 70);
      doc.text(`Generated by MBEOWS | ${generationDate}`, pageWidth - 10, 10, { align: "right" });

      /* SUMMARY (SINGLE ROW – SPACE SAVING) */
      const total = pdfData.length;
      const newCount = pdfData.filter(e => e.type === "new").length;
      const replaceCount = pdfData.filter(e => e.type === "replace").length;
      const approved = pdfData.filter(e => e.status === "approved").length;
      const pending = pdfData.filter(e => e.status === "pending").length;
      doc.setFontSize(9);
      doc.setTextColor(25, 25, 25);
      doc.setFont("helvetica", "bold");
      doc.text(
        `Month/Year: ${pdfFilters.month} / ${pdfFilters.year}   |   Total: ${total}   |   New: ${newCount}   |   Replace: ${replaceCount}   |   Approved: ${approved}   |   Pending: ${pending}`,
        10,
        18
      );
      doc.line(4, 22, pageWidth - 4, 22);

      /* TABLE (FULL DATA INCLUDED) */
      const rows = pdfData.map((item, index) => [
        index + 1,
        `${item.entryId} / ${item?.agreementId || "-"}`,
        formatDateTime(item.date),
        formatLabel(item.category),
        `${formatLabel(item.type)} / ${item.placementIndex ?? "-"}`,
        formatLabel(item.status),
        `ID: ${item.employee?.employeeId || "-"}\nName: ${item.employee?.name || "-"}\nF/H: ${item.employee?.father_husband_name || "-"}`,
        `ID: ${item.client?.clientId || "-"}\nName: ${item.client?.name || "-"}\nAddress: ${item.client?.address || "-"}`,
      ]);
      autoTable(doc, {
        startY: 26,
        head: [[
          "#",
          "ID/A-ID",
          "Date",
          "Category",
          "Type/P#",
          "Status",
          "Employee",
          "Client",
        ]],
        body: rows,
        styles: {
          fontSize: 7,
          cellPadding: 2,
          valign: "middle",
        },
        headStyles: {
          fillColor: [30, 64, 175],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        theme: "grid",
        margin: { left: 4, right: 4 },
      });

      doc.save(`Entry_Report_${pdfFilters.month}_${pdfFilters.year}.pdf`);
      toast.success('PDF Generated');
    } catch (err) {
      console.log("Error generating monthly entries PDF:", err);
      toast.error("Failed to generate monthly PDF!")
    } finally {
      setIsLoading(false);
      return;
    }
  };

  // Agreement State
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Fallback control state
  const hasViewData = Object.keys(formData1 || {}).length > 0;
  const hasUpdateData = formData2?.id;

  // Close menu list on outside click
  useEffect(() => {
    if (!menuState.open) return;
    const handleOutside = (e) => {
      const isTrigger = e.target.closest('[data-menu-trigger="true"]');
      const isMenu = e.target.closest('[data-floating-menu="true"]');
      if (isTrigger || isMenu) return;
      closeMenu();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", closeMenu);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", closeMenu);
    };
  }, [menuState.open]);

  // actions btn list
  const menuActionList = [
    {
      label: "Agreement", type: "agreement", color: "text-purple-600", hover: "hover:bg-purple-100",
      boxType: "info", message: "Do you want to create an agreement for this entry?",
    },
    {
      label: "Edit", type: "edit", color: "text-blue-600", hover: "hover:bg-blue-100",
      boxType: "info", message: "Do you want to edit this entry details?",
    },
    {
      label: "Delete", type: "delete", color: "text-red-600", hover: "hover:bg-red-100",
      boxType: "danger",
      message: "This will permanently delete the entry. This action cannot be undone.",
    },
  ];

  return (
    <>
      <ConfirmDialog
        isOpen={dialog.open}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.action}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        type={dialog.type}
      />
      <EntitySelector
        open={selector.open}
        onClose={() => setSelector({ open: false, type: null })}
        label={selector.type}
        apiPath={
          selector.type === "employee"
            ? "/employee/selector-search"
            : selector.type === "client"
              ? "/client/selector-search"
              : `/agreement/get-client/${formData.clientRef || formData2.clientRef}`
        }
        displayFields={
          selector.type === "employee"
            ? ["name", "father_husband_name", "phone"]
            : selector.type === "client"
              ? ["name", "phone", "address"]
              : ["agreementId", "category", "agreementDate", "employee", "client"]
        }
        onSelect={handleEntitySelect}
      />
      <OpCodeDialog
        isOpen={opCodeDialog.open}
        type="danger"
        title="Process Authorization"
        message="By proceeding, you accept responsibility for any resulting data loss or modification. Please enter the OpCode to continue."
        confirmText="Verify"
        onClose={() =>
          setOpCodeDialog({ open: false, id: null })
        }
        onConfirm={(code) => {
          handleDelete(opCodeDialog.id, code);
        }}
      />
      {/* Three Dot Menu UI */}
      {menuState.open && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div
            data-floating-menu="true"
            className="absolute bg-white border shadow-xl rounded-xl p-2 w-44 space-y-1 pointer-events-auto"
            style={{
              top: menuState.position.top,
              left: menuState.position.left,
            }}
          >
            {menuActionList.filter(action => {
              if (action.type === "agreement") return menuState.rowData?.showAgreementBtn;
              return true;
            }).map((action) => (
              <button
                key={action.type}
                className={`flex items-center w-full gap-2 p-2 rounded-lg ${action.hover} text-sm font-medium cursor-pointer`}
                onClick={() => {
                  closeMenu();
                  if (action.type === "agreement") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "Agreement Creation",
                      message: action.message,
                      confirmText: "Create",
                      action: () => {
                        setSelectedEntry(menuState.rowId);
                        setIsAgreementOpen(true);
                      },
                    });
                    return;
                  }
                  if (action.type === "edit") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "Edit Entry",
                      message: action.message,
                      confirmText: action.label,
                      action: () => handleUpdateOpen(menuState.rowId),
                    });
                    return;
                  }
                  setDialog({
                    open: true,
                    type: action.boxType,
                    title: "Delete Entry",
                    message: action.message,
                    confirmText: "Proceed",
                    action: () => {
                      setOpCodeDialog({
                        open: true,
                        id: menuState.rowId,
                      });
                    },
                  });
                }}
              >
                <span className={action.color}>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Details & Update UI */}
      {isViewOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Entry Details</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              {loaders.details ? (
                <div className="flex justify-center items-center h-[45vh]">
                  <Loader />
                </div>
              ) : !hasViewData ? (
                <FallBack
                  title="Entry details not available"
                  message="This entry record could not be loaded. It may have been removed, or there was a network issue."
                  actionText="Close"
                  onAction={handleViewClose}
                />
              ) : (
                <div className="flex-1 space-y-4 overflow-auto p-3 bg-gray-50">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-6 mt-6 text-sm">
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                          ID: {formData1?.entryId}
                        </h2>
                      </div>

                      <div>
                        <p className="text-gray-500">Agreement ID</p>
                        <p className="font-medium text-gray-800">
                          {formData1?.agreementRef?.agreementId || "-"}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-medium text-gray-800">
                          {formatDateTime(formData1?.date)}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Category</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.category)}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Type</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.type)}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Placement #</p>
                        <p className="font-medium text-gray-800">{formData1?.placementIndex}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Client Name</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.clientName) || "-"}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Client Phone</p>
                        <p className="font-medium text-gray-800">{formData1?.clientPhone || "-"}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Client Address</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.clientAddress) || "-"}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Status</p>
                        <span
                          className={`inline-flex items-center px-3 py-1 mt-1 text-xs font-medium rounded-full w-fit
                              ${formData1?.status === "approved" && "bg-green-100 text-green-700"}
                              ${formData1?.status === "pending" && "bg-blue-100 text-blue-700"}
                        `}>
                          {formatLabel(formData1?.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-700">
                          {formData1?.employeeRef?.name?.charAt(0) || "E"}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {formData1?.employeeRef?.name}
                          </h3>
                          <p className="text-sm text-gray-500">Employee</p>
                        </div>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">ID</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.employeeRef?.employeeId}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">F/H</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.employeeRef?.father_husband_name}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">Phone</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.employeeRef?.phone}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-xl font-bold text-indigo-700">
                          {formData1?.clientRef?.name?.charAt(0) || "C"}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {formData1?.clientRef?.name}
                          </h3>
                          <p className="text-sm text-gray-500">Client - Main</p>
                        </div>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">ID</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.clientRef?.clientId}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">Phone</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.clientRef?.phone}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">Address</span>
                          <span className="font-medium text-gray-800 text-right max-w-[65%]">
                            {formData1?.clientRef?.address}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {hasViewData && (
                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex items-center justify-between">
                  <span className='text-xs'>
                    <span className="font-medium text-gray-600">Updated At:</span>{" "}
                    {formatTableDateTime(formData1.updatedAt)}
                  </span>
                  <button
                    type='button'
                    onClick={() => handleViewClose()}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 hover:bg-blue-500 text-white font-semibold transition"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isUpdateOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Update Entry</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              {loaders.update ? (
                <div className="flex justify-center items-center h-[45vh]">
                  <Loader />
                </div>
              ) : !hasUpdateData ? (
                <FallBack
                  title="Unable to load entry for update"
                  message="The entry data could not be loaded for editing. Please refresh or try again."
                  actionText="Close"
                  onAction={handleUpdateClose}
                />
              ) : (
                <div className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
                  <fieldset className="bg-white border p-4 rounded-lg">
                    <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Entry Info</legend>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <Select
                          name="category"
                          options={categoryOptions}
                          placeholder="Select Category"
                          value={categoryOptions.find(option => option.value === formData2.category)}
                          onChange={(selectedOption) =>
                            handleSelectUpdate(selectedOption, { name: 'category' })
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>
                        <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                          type="datetime-local"
                          name='date'
                          value={formData2.date}
                          onChange={handleChangeUpdate}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Type</label>
                        <Select
                          name="type"
                          options={entryTypeOptions}
                          placeholder="Select Type"
                          value={entryTypeOptions.find(option => option.value === formData2.type)}
                          onChange={(selectedOption) =>
                            handleSelectUpdate(selectedOption, { name: 'type' })
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <Select
                          name="status"
                          options={entryStatusOptions}
                          placeholder="Select Status"
                          value={entryStatusOptions.find(option => option.value === formData2.status)}
                          onChange={(selectedOption) =>
                            handleSelectUpdate(selectedOption, { name: 'status' })
                          }
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => openSelector("employee", "update")}
                        className="w-full flex items-center justify-between px-4 py-3 border rounded-xl bg-white hover:bg-gray-50 transition shadow-sm"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <UserIcon className="h-5 w-5 text-blue-600" />
                          <div className="flex flex-col text-left min-w-0">
                            <span className="text-xs text-gray-500">Employee</span>
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {formData2.employeeObj?.name || "Select Employee"}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {formData2.employeeRef ? "Change" : ""}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => openSelector("client", "update")}
                        className="w-full flex items-center justify-between px-4 py-3 border rounded-xl bg-white hover:bg-gray-50 transition shadow-sm"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />
                          <div className="flex flex-col text-left min-w-0">
                            <span className="text-xs text-gray-500">Client</span>
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {formData2.clientObj?.name || "Select Client"}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {formData2.clientRef ? "Change" : ""}
                        </span>
                      </button>

                      {formData2.clientRef && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Client Name</label>
                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                              placeholder="Full Name"
                              type="text"
                              name='clientName'
                              value={formData2.clientName}
                              onChange={handleChangeUpdate}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Client Phone</label>
                            <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-400 transition">
                              <select
                                value={formData2.countryCode}
                                onChange={(e) =>
                                  setFormData2({
                                    ...formData2,
                                    countryCode: e.target.value,
                                    clientPhone: "",
                                  })
                                }
                                className="w-[88px] shrink-0 px-2 py-2 text-sm bg-transparent border-r border-gray-200 focus:outline-none cursor-pointer"
                              >
                                {countriesOptions.map((c) => (
                                  <option key={c.code} value={c.code}>
                                    {c.code} {c.dial}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="tel"
                                inputMode="numeric"
                                placeholder="Phone number"
                                value={formData2.clientPhone}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, "");
                                  const selectedCountry = countriesOptions.find(
                                    (c) => c.code === formData2.countryCode
                                  );
                                  const maxLength = selectedCountry?.maxLength || 15;
                                  setFormData2({
                                    ...formData2,
                                    clientPhone: digits.slice(0, maxLength),
                                  });
                                }}
                                className="flex-1 px-3 py-2 outline-none bg-transparent text-sm"
                              />
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              Include number only — country code is selected separately
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">Client Address</label>
                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                              placeholder="H#01, ST#01, ABC..."
                              type="text"
                              name='clientAddress'
                              value={formData2.clientAddress}
                              onChange={handleChangeUpdate}
                            />
                          </div>
                        </>
                      )}

                      {formData2.type === "replace" && (
                        <button
                          type="button"
                          onClick={() => openSelector("agreement", "update")}
                          disabled={!formData2.clientRef}
                          className={`w-full flex items-center justify-between px-4 py-3 border rounded-xl transition shadow-sm
                                ${formData2.clientRef
                              ? "bg-white hover:bg-gray-50"
                              : "bg-gray-100 cursor-not-allowed opacity-60"
                            }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                            <div className="flex flex-col text-left min-w-0">
                              <span className="text-xs text-gray-500">Agreement</span>
                              <span className="text-sm font-medium text-gray-800 truncate">
                                {formData2.agreementObj?.agreementId || "Select Agreement"}
                              </span>
                            </div>
                          </div>

                          <span className="text-xs text-gray-400">
                            {formData2.agreementRef ? "Change" : ""}
                          </span>
                        </button>
                      )}
                    </div>
                  </fieldset>
                </div>
              )}
              {!loaders.update && hasUpdateData && (
                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                  <button
                    type='button'
                    onClick={() => handleUpdateClose()}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => setDialog({
                      open: true,
                      title: "Confirm Update",
                      message: "Are you sure you want to update this entrie’s information? Changes will be saved immediately.",
                      confirmText: "Update",
                      type: "warning",
                      action: () => handleUpdate(formData2.id),
                    })}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    Update
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* PDF Generation UI */}
      {isPdfOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Monthly Entry Report</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              <div className="flex-1 overflow-auto p-3 bg-gray-50 space-y-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Month</label>
                    <select
                      name="month"
                      value={pdfFilters.month}
                      onChange={handlePdfFilterChange}
                      className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                    >
                      <option value="">Select Month</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleString("default", { month: "long" })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Year</label>
                    <select
                      name="year"
                      value={pdfFilters.year}
                      onChange={handlePdfFilterChange}
                      className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                    >
                      <option value="">Select Year</option>
                      {Array.from({ length: 5 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={handlePdfFetch}
                      className="w-full px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition"
                    >
                      Filter
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border p-2.5">
                  {loaders.pdfFilter ? (
                    <div className="flex justify-center items-center py-10">
                      <Loader />
                    </div>
                  ) : pdfData.length === 0 ? (
                    <p className="text-center text-gray-500 py-6">
                      No Data!
                    </p>
                  ) : (
                    <div className="max-h-[45vh] overflow-auto">
                      <table className="w-full text-sm text-center">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 border-b">ID</th>
                            <th className="p-3 border-b">A-ID</th>
                            <th className="p-3 border-b">Date</th>
                            <th className="p-3 border-b">Employee</th>
                            <th className="p-3 border-b">Client</th>
                            <th className="p-3 border-b">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pdfData.map((item, index) => (
                            <tr key={index} className="even:bg-gray-50">
                              <td className="p-3 border-b">{item.entryId}</td>
                              <td className="p-3 border-b">{item.agreementId || '-'}</td>
                              <td className="p-3 border-b">{formatTableDateTime(item.date)}</td>
                              <td className="p-3 border-b">{item.employee?.name}</td>
                              <td className="p-3 border-b">{item.client?.name}</td>
                              <td className="p-3 border-b">{formatLabel(item.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                <button
                  onClick={handlePdfClose}
                  className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                >
                  Close
                </button>
                {pdfData.length > 0 && (
                  <button
                    onClick={() => {
                      setDialog({
                        open: true,
                        type: "info",
                        title: "PDF Generation",
                        message: "Do you want to generate the PDF report for the displayed entries?",
                        confirmText: "Generate",
                        action: () => handleMonthlyPdf(),
                      })
                    }}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition bg-green-600 text-white hover:bg-green-700"
                  >
                    Generate
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Agreement UI */}
      <AgreementAdd
        open={isAgreementOpen}
        onClose={() => setIsAgreementOpen(false)}
        entry={selectedEntry}
      />
      <div className="w-full">
        {/* Heading */}
        <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              className="md:hidden p-2 bg-white shadow rounded-lg"
              onClick={() => setIsMobileOpen(true)}
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
            </button>
            <div className="flex flex-wrap items-center">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                En
              </h1>
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                try
              </span>
            </div>
          </div>

          <div>
            <button
              className="mr-2 px-4 sm:px-6 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 font-medium text-sm sm:text-base"
              onClick={handlePdfOpen}
            >
              PDF
            </button>
            <button
              className="px-4 sm:px-6 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 font-medium text-sm sm:text-base"
              onClick={handleAddOpen}
            >
              Add
            </button>
            {isAddDialog && (
              <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
                <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
                  <div className="max-h-[85vh] flex flex-col">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                      <h2 className="text-xl sm:text-2xl font-bold">Create Entry</h2>
                      <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
                    </div>
                    <div className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
                      <fieldset className="bg-white border p-4 rounded-lg">
                        <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Entry Info</legend>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Category</label>
                            <Select
                              name="category"
                              options={categoryOptions}
                              placeholder="Select Category"
                              value={categoryOptions.find(option => option.value === formData.category)}
                              onChange={(selectedOption) =>
                                handleSelectChange(selectedOption, { name: 'category' })
                              }
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">Date</label>
                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                              type="datetime-local"
                              name='date'
                              value={formData.date}
                              onChange={onChange}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">Type</label>
                            <Select
                              name="type"
                              options={entryTypeOptions}
                              placeholder="Select Type"
                              value={entryTypeOptions.find(option => option.value === formData.type)}
                              onChange={(selectedOption) =>
                                handleSelectChange(selectedOption, { name: 'type' })
                              }
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <Select
                              name="status"
                              options={entryStatusOptions}
                              placeholder="Select Status"
                              value={entryStatusOptions.find(option => option.value === formData.status)}
                              onChange={(selectedOption) =>
                                handleSelectChange(selectedOption, { name: 'status' })
                              }
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => openSelector("employee", "create")}
                            className="w-full flex items-center justify-between px-4 py-3 border rounded-xl bg-white hover:bg-gray-50 transition shadow-sm"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <UserIcon className="h-5 w-5 text-blue-600" />
                              <div className="flex flex-col text-left min-w-0">
                                <span className="text-xs text-gray-500">Employee</span>
                                <span className="text-sm font-medium text-gray-800 truncate">
                                  {formData.employeeObj?.name || "Select Employee"}
                                </span>
                              </div>
                            </div>
                            <span className="text-xs text-gray-400">
                              {formData.employeeRef ? "Change" : ""}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => openSelector("client", "create")}
                            className="w-full flex items-center justify-between px-4 py-3 border rounded-xl bg-white hover:bg-gray-50 transition shadow-sm"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />
                              <div className="flex flex-col text-left min-w-0">
                                <span className="text-xs text-gray-500">Client</span>
                                <span className="text-sm font-medium text-gray-800 truncate">
                                  {formData.clientObj?.name || "Select Client"}
                                </span>
                              </div>
                            </div>
                            <span className="text-xs text-gray-400">
                              {formData.clientRef ? "Change" : ""}
                            </span>
                          </button>

                          {formData.clientRef && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Client Name</label>
                                <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                  placeholder="Full Name"
                                  type="text"
                                  name='clientName'
                                  value={formData.clientName}
                                  onChange={onChange}
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Client Phone</label>
                                <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-400 transition">
                                  <select
                                    value={formData.countryCode}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        countryCode: e.target.value,
                                        clientPhone: "",
                                      })
                                    }
                                    className="w-[88px] shrink-0 px-2 py-2 text-sm bg-transparent border-r border-gray-200 focus:outline-none cursor-pointer"
                                  >
                                    {countriesOptions.map((c) => (
                                      <option key={c.code} value={c.code}>
                                        {c.code} {c.dial}
                                      </option>
                                    ))}
                                  </select>
                                  <input
                                    type="tel"
                                    inputMode="numeric"
                                    placeholder="Phone number"
                                    value={formData.clientPhone}
                                    onChange={(e) => {
                                      const digits = e.target.value.replace(/\D/g, "");
                                      const selectedCountry = countriesOptions.find(
                                        (c) => c.code === formData.countryCode
                                      );
                                      const maxLength = selectedCountry?.maxLength || 15;
                                      setFormData({
                                        ...formData,
                                        clientPhone: digits.slice(0, maxLength),
                                      });
                                    }}
                                    className="flex-1 px-3 py-2 outline-none bg-transparent text-sm"
                                  />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  Include number only — country code is selected separately
                                </p>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700">Client Address</label>
                                <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                  placeholder="H#01, ST#01, ABC..."
                                  type="text"
                                  name='clientAddress'
                                  value={formData.clientAddress}
                                  onChange={onChange}
                                />
                              </div>
                            </>
                          )}

                          {formData.type === "replace" && (
                            <button
                              type="button"
                              onClick={() => openSelector("agreement", "create")}
                              disabled={!formData.clientRef}
                              className={`w-full flex items-center justify-between px-4 py-3 border rounded-xl transition shadow-sm
                                ${formData.clientRef
                                  ? "bg-white hover:bg-gray-50"
                                  : "bg-gray-100 cursor-not-allowed opacity-60"
                                }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                                <div className="flex flex-col text-left min-w-0">
                                  <span className="text-xs text-gray-500">Agreement</span>
                                  <span className="text-sm font-medium text-gray-800 truncate">
                                    {formData.agreementObj?.agreementId || "Select Agreement"}
                                  </span>
                                </div>
                              </div>

                              <span className="text-xs text-gray-400">
                                {formData.agreementRef ? "Change" : ""}
                              </span>
                            </button>
                          )}
                        </div>
                      </fieldset>
                    </div>
                    <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                      <button
                        type='button'
                        onClick={() => handleAddClose()}
                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <hr className="border-t border-gray-200 my-4" />

        {/* Data Display Code */}
        <div className='w-full'>
          {/* Quick & Deep Search Bar */}
          <div className="w-full flex justify-center mb-7">
            <form
              onSubmit={handleSearchSubmit}
              className="relative w-full max-w-2xl flex items-center"
            >
              {/* Search Input */}
              <div className="relative flex-1">
                <input
                  type="search"
                  placeholder={`${searchMode === "quick" ? "Quick" : "Deep"} search . . .`}
                  value={query}
                  onChange={handleQuickSearch}
                  className="w-full py-3 pl-10 sm:pl-12 pr-32 sm:pr-40 rounded-2xl bg-white/80 backdrop-blur-sm 
                             border border-gray-200 shadow-md focus:ring-2 focus:ring-indigo-400 
                             focus:outline-none text-gray-700 placeholder-gray-400 transition"
                />
                <MagnifyingGlassIcon className="absolute w-5 h-5 left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              {/* Mode Toggle */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex rounded-full overflow-hidden border border-gray-200 shadow">
                <button
                  type="button"
                  onClick={() => setSearchMode("quick")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "quick"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Quick
                </button>
                <button
                  type="button"
                  onClick={() => setSearchMode("deep")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "deep"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Deep
                </button>
              </div>
            </form>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto h-[70vh]">
            {(loaders.fetch || loaders.search) ? (
              <div className="flex justify-center items-center h-full p-6">
                <Loader />
              </div>
            ) : displayData.length === 0 ? (
              <div className="flex justify-center items-center h-full p-6">
                <p className="font-semibold text-gray-600 text-xl sm:text-md">
                  {searchMode === "quick" && query.trim()
                    ? "No Match !!"
                    : searchMode === "deep"
                      ? "No Records !!"
                      : "No Entry !!"}
                </p>
              </div>
            ) : (
              <>
                <table className="w-full bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        "#",
                        "ID",
                        "Date",
                        "Category",
                        "Employee",
                        "Client",
                        "Status",
                        "Created",
                        "Action",
                      ].map((header, index) => (
                        <th
                          key={index}
                          className="p-3 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((data, index) => (
                      <tr key={data._id || index} className="hover:bg-gray-50 even:bg-gray-100">
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.entryId}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatTableDateTime(data.date)}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatLabel(data.category)}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.employee || '-'}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.client || '-'}
                        </td>
                        <td className="p-3 text-sm text-center border-b border-gray-200">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full w-fit
                              ${data?.status === "approved" && "bg-green-100 text-green-700"}
                              ${data?.status === "pending" && "bg-blue-100 text-blue-700"}
                          `}>
                            {formatLabel(data.status)}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatTableDateTime(data.createdAt)}
                        </td>
                        <td className="p-3 text-center border-b border-gray-200">
                          <div className="flex justify-center space-x-2">
                            <button
                              type='button'
                              className="px-2 sm:px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600 font-medium text-sm sm:text-base"
                              onClick={() => {
                                setDialog({
                                  open: true,
                                  type: "info",
                                  title: "View Details",
                                  message: "Want to view details for this entry?",
                                  confirmText: "View",
                                  action: () => handleViewOpen(data._id),
                                })
                              }}
                            >
                              Details
                            </button>

                            <button
                              type="button"
                              data-menu-trigger="true"
                              className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (menuState.open && menuState.rowId === data._id) {
                                  closeMenu();
                                  return;
                                }
                                const rect = e.currentTarget.getBoundingClientRect();
                                openMenu({
                                  id: data._id,
                                  row: data,
                                  position: {
                                    top: rect.bottom + window.scrollY - (data.showAgreementBtn ? 180 : 140),
                                    left: rect.right + window.scrollX - 180,
                                  },
                                });
                              }}
                            >
                              <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

          </div>

          {/* Pagination */}
          {searchMode === "quick" && totalPages > 1 && (
            <div className="w-full flex flex-col sm:flex-row justify-between items-center p-4 gap-2 sm:gap-4">
              {/* Showing Entries Text */}
              <span className="text-sm text-gray-600 text-center sm:text-left">
                Showing {startIndex} to {endIndex} of {totalBackendPg} entries
              </span>
              {/* Pagination Buttons */}
              <div className="flex flex-wrap justify-center sm:justify-end gap-1 sm:gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={loaders.fetch || currentPage === 1}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                {/* Page Numbers */}
                {(() => {
                  const buttons = [];
                  const pageRange = 1; // current ±2 pages
                  let startPage = Math.max(currentPage - pageRange, 1);
                  let endPage = Math.min(currentPage + pageRange, totalPages);

                  if (startPage > 1) {
                    buttons.push(
                      <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) buttons.push(<span key="start-ellipsis" className="px-2 hidden sm:inline">...</span>);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    buttons.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) buttons.push(<span key="end-ellipsis" className="px-2 hidden sm:inline">...</span>);
                    buttons.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  return buttons;
                })()}
                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={loaders.fetch || currentPage === totalPages}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Entry;